
import React from 'react';
import { SimulationConfig } from './types';
import { SectionLabel } from './SectionLabel';
import { SmartInput } from './SmartInput';
import { ControlGroup } from './ControlGroup';

interface TitleFooterSettingsSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
}

const ColorInput = ({ label, value, onChange, darkMode }: { label: string, value: string, onChange: (v: string) => void, darkMode: boolean }) => (
  <ControlGroup label={label}>
      <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full border border-white/20 shadow-sm" style={{ backgroundColor: value }} />
          <input type="color" value={value} onChange={e => onChange(e.target.value)} className="flex-1 h-8 opacity-0 absolute cursor-pointer" />
          <input type="text" value={value} onChange={e => onChange(e.target.value)} className={`flex-1 px-2 py-1 text-[9px] font-mono border rounded-lg ${darkMode ? 'bg-neutral-800 border-neutral-700' : 'bg-white border-neutral-200'}`} />
      </div>
  </ControlGroup>
);

const FONT_FAMILIES = [
  'Inter', 'Space Grotesk', 'Outfit', 'Playfair Display', 'JetBrains Mono', 'Fira Code', 'Roboto', 'Montserrat', 'Open Sans'
];

const FONT_WEIGHTS = [
  { label: 'Thin', value: '100' },
  { label: 'Light', value: '300' },
  { label: 'Normal', value: '400' },
  { label: 'Medium', value: '500' },
  { label: 'Semi-Bold', value: '600' },
  { label: 'Bold', value: '700' },
  { label: 'Extra-Bold', value: '800' },
  { label: 'Black', value: '900' }
];

export const TitleFooterSettingsSection: React.FC<TitleFooterSettingsSectionProps> = ({ config, setConfig }) => {
  return (
    <div className="space-y-6">
      <div>
        <SectionLabel icon="fa-heading" darkMode={config.darkMode}>Title Settings</SectionLabel>
        <div className="space-y-4">
          <div className="space-y-1">
            <label className={`text-[10px] font-black uppercase tracking-widest ${config.darkMode ? 'text-neutral-500' : 'text-neutral-400'}`}>Title Text</label>
            <input 
              type="text" 
              value={config.titleText} 
              onChange={(e) => setConfig(prev => ({ ...prev, titleText: e.target.value }))} 
              className={`w-full px-3 py-2 border rounded-xl text-xs font-bold outline-none transition-all ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white focus:border-blue-500' : 'bg-neutral-50 border-neutral-200 text-neutral-800 focus:border-blue-400'}`} 
              placeholder="Enter title text..." 
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className={`text-[10px] font-black uppercase tracking-widest ${config.darkMode ? 'text-neutral-500' : 'text-neutral-400'}`}>Font Family</label>
              <select 
                value={config.titleFontFamily} 
                onChange={(e) => setConfig(prev => ({ ...prev, titleFontFamily: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-xl text-xs font-bold outline-none transition-all ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-neutral-50 border-neutral-200 text-neutral-800'}`}
              >
                {FONT_FAMILIES.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className={`text-[10px] font-black uppercase tracking-widest ${config.darkMode ? 'text-neutral-500' : 'text-neutral-400'}`}>Font Weight</label>
              <select 
                value={config.titleFontWeight} 
                onChange={(e) => setConfig(prev => ({ ...prev, titleFontWeight: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-xl text-xs font-bold outline-none transition-all ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-neutral-50 border-neutral-200 text-neutral-800'}`}
              >
                {FONT_WEIGHTS.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <SmartInput label="Font Size" min={10} max={120} value={config.titleFontSize} onChange={(v: number) => setConfig(p => ({...p, titleFontSize: v}))} darkMode={config.darkMode} />
            <ColorInput label="Text Color" value={config.titleColor} onChange={(v: string) => setConfig(p => ({...p, titleColor: v}))} darkMode={config.darkMode} />
          </div>
        </div>
      </div>

      <div className="pt-4 border-t border-neutral-200 dark:border-neutral-800">
        <SectionLabel icon="fa-paragraph" darkMode={config.darkMode}>Footer Settings</SectionLabel>
        <div className="space-y-4">
          <div className="space-y-1">
            <label className={`text-[10px] font-black uppercase tracking-widest ${config.darkMode ? 'text-neutral-500' : 'text-neutral-400'}`}>Footer Text</label>
            <input 
              type="text" 
              value={config.footerText} 
              onChange={(e) => setConfig(prev => ({ ...prev, footerText: e.target.value }))} 
              className={`w-full px-3 py-2 border rounded-xl text-xs font-bold outline-none transition-all ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white focus:border-blue-500' : 'bg-neutral-50 border-neutral-200 text-neutral-800 focus:border-blue-400'}`} 
              placeholder="Enter footer text..." 
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className={`text-[10px] font-black uppercase tracking-widest ${config.darkMode ? 'text-neutral-500' : 'text-neutral-400'}`}>Font Family</label>
              <select 
                value={config.footerFontFamily} 
                onChange={(e) => setConfig(prev => ({ ...prev, footerFontFamily: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-xl text-xs font-bold outline-none transition-all ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-neutral-50 border-neutral-200 text-neutral-800'}`}
              >
                {FONT_FAMILIES.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className={`text-[10px] font-black uppercase tracking-widest ${config.darkMode ? 'text-neutral-500' : 'text-neutral-400'}`}>Font Weight</label>
              <select 
                value={config.footerFontWeight} 
                onChange={(e) => setConfig(prev => ({ ...prev, footerFontWeight: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-xl text-xs font-bold outline-none transition-all ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-neutral-50 border-neutral-200 text-neutral-800'}`}
              >
                {FONT_WEIGHTS.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <SmartInput label="Font Size" min={8} max={60} value={config.footerFontSize} onChange={(v: number) => setConfig(p => ({...p, footerFontSize: v}))} darkMode={config.darkMode} />
            <ColorInput label="Text Color" value={config.footerTextColor} onChange={(v: string) => setConfig(p => ({...p, footerTextColor: v}))} darkMode={config.darkMode} />
          </div>
        </div>
      </div>
    </div>
  );
};
